import React, { useState, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useQuote } from './Quote/QuoteContext';
import { auth, db } from '../utils/firebase';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { useUserScheme } from './UserSchemeContext';

// ── ProductCard wrapped in React.memo ─────────────────────────────────────────
// React.memo prevents re-renders when parent (Products page) scrolls or updates
// unrelated state. Cards only re-render when their own props change.
const ProductCard = memo(function ProductCard({ product, onAddToCart, isLiked, onToggleMyList }) {
    const [quantity, setQuantity] = useState(() => product.minimum_order || 1);
    const cardRef = useRef(null);
    const { openQuote, cart = [], handleUpdateCartQty } = useQuote();

    // schemeId is now fetched ONCE via context (UserSchemeProvider in App.js)
    // instead of once per card — eliminates N Firestore listeners during scroll
    const schemeId = useUserScheme();

    const openQuotePopup = (product) => {
        if (!auth.currentUser) {
            toast.error("Please sign in to request a quote.");
            window.dispatchEvent(new Event('open-auth'));
            return;
        }
        openQuote(product);
    };

    const minimum_order = product.minimum_order || 1;

    const handleDecrement = () => {
        if (cartQuantity > 0) {
            const newQty = cartQuantity - minimum_order;
            if (newQty < minimum_order) {
                handleUpdateCartQty(product, 0);
            } else {
                handleUpdateCartQty(product, newQty);
            }
        } else {
            const num = Number(quantity) || minimum_order;
            if (num <= minimum_order) {
                toast.error(`Minimum order quantity is ${minimum_order}`);
                setQuantity(minimum_order);
            } else {
                setQuantity(num - minimum_order);
            }
        }
    };

    const handleIncrement = () => {
        if (cartQuantity > 0) {
            handleUpdateCartQty(product, cartQuantity + minimum_order);
        } else {
            const num = Number(quantity) || 0;
            setQuantity(num + minimum_order);
        }
    };

    const displayTitle = product.productName || product.title || '';
    const displayImage = product.image || (product.images?.[0]) || '/product-placeholder.png';
    const displayCode = product.productCodeLetters && product.productCode
        ? `${product.productCodeLetters}${product.productCode}`
        : (product.code || '');
    const displayBrand = product.brandName || product.brand || 'CHEFCO';
    const displayType = product.type_name || product.type || product.Name || 'PACKAGING';
    const displayPack = product.prices || product.pack || product.unitsOfMeasurement || 'Nos';

    const getProductCode = (p) => {
        if (!p) return '';
        if (p.code) return p.code;
        if (p.productCodeLetters && p.productCode) return `${p.productCodeLetters}${p.productCode}`;
        return p.productCode || '';
    };

    const getProductTitle = (p) => {
        if (!p) return '';
        return p.title || p.productName || '';
    };

    const getProductId = (p) => {
        if (!p) return '';
        return p.id || p.objectID || '';
    };

    const areProductsEqual = (p1, p2) => {
        const id1 = getProductId(p1); const id2 = getProductId(p2);
        if (id1 && id2) return id1 === id2;
        const code1 = getProductCode(p1); const code2 = getProductCode(p2);
        if (code1 && code2) return code1 === code2;
        const title1 = getProductTitle(p1); const title2 = getProductTitle(p2);
        return !!(title1 && title2 && title1 === title2);
    };

    const cartItem = cart?.find((item) => areProductsEqual(item.product, product));
    const cartQuantity = cartItem ? cartItem.quantity : 0;

    const getDisplayPrice = () => {
        if (!product || !schemeId) return 0;

        let schemePricing = null;

        // 1. Direct match by scheme_id / schema_id / schemeId in product.pricing array
        if (product.pricing && Array.isArray(product.pricing)) {
            schemePricing = product.pricing.find(p =>
                p && String(p.scheme_id || p.schema_id || p.schemeId || '') === String(schemeId)
            );
        }

        // 2. Index match via product.schemeIds array
        if (!schemePricing && product.schemeIds && Array.isArray(product.schemeIds) && product.pricing && Array.isArray(product.pricing)) {
            const schemeIdx = product.schemeIds.findIndex(id => String(id) === String(schemeId));
            if (schemeIdx !== -1 && product.pricing[schemeIdx]) {
                schemePricing = product.pricing[schemeIdx];
            }
        }

        // If matching scheme pricing exists and has valid tiers
        if (schemePricing && schemePricing.tiers && schemePricing.tiers.length > 0) {
            const qty = cartQuantity > 0 ? cartQuantity : (Number(quantity) || product.minimum_order || 1);
            const activeTier = schemePricing.tiers.find(tier => {
                const min = Number(tier.min ?? tier.minQty ?? 0);
                const max = Number(tier.max ?? tier.maxQty ?? Infinity);
                return qty >= min && qty <= max;
            });

            if (activeTier && activeTier.price !== undefined && activeTier.price !== null && activeTier.price !== '' && Number(activeTier.price) > 0) {
                return Number(activeTier.price);
            }
            if (schemePricing.tiers[0] && schemePricing.tiers[0].price !== undefined && schemePricing.tiers[0].price !== null && schemePricing.tiers[0].price !== '' && Number(schemePricing.tiers[0].price) > 0) {
                return Number(schemePricing.tiers[0].price);
            }
        }

        return 0;
    };

    const finalPrice = getDisplayPrice();
    const hasPricingForScheme = typeof finalPrice === 'number' && finalPrice > 0;
    const formattedPrice = hasPricingForScheme
        ? (finalPrice % 1 === 0 ? finalPrice : finalPrice.toFixed(2))
        : '0.00';

    const currentUser = auth.currentUser;
    const isCurrentlyLiked = isLiked !== undefined
        ? isLiked
        : !!(currentUser && product.wishlistIds?.includes(currentUser.uid));

    const handleLikeClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!auth.currentUser) {
            toast.error("Please sign in to add items to your wishlist.");
            window.dispatchEvent(new Event('open-auth'));
            return;
        }

        const uid = auth.currentUser.uid;
        const prodId = getProductId(product);
        if (!prodId) { console.error("Product has no valid ID:", product); return; }

        const productRef = doc(db, "products", prodId);
        const nextLikedState = !isCurrentlyLiked;

        try {
            if (nextLikedState) {
                await updateDoc(productRef, { wishlistIds: arrayUnion(uid) });
                toast.success("Added to wishlist");
            } else {
                await updateDoc(productRef, { wishlistIds: arrayRemove(uid) });
                toast.success("Removed from wishlist");
            }
        } catch (error) {
            console.error("Error updating wishlist in Firestore:", error);
            toast.error("Failed to update wishlist");
        }

        if (onToggleMyList) onToggleMyList();
    };

    return (
        /* Use specific CSS transitions (not transition-all) to avoid layout
           thrashing during scroll — only transition transform and shadow */
        <div
            ref={cardRef}
            className="group relative overflow-hidden rounded-[24px] bg-white p-2.5 border border-[#D7DDD4] shadow-[0_20px_50px_rgba(36,49,38,0.02)] flex flex-col justify-between"
            style={{
                // GPU-composited transitions: only transform & box-shadow, not 'all'
                // This prevents the browser from repainting during scroll
                transition: 'transform 0.4s ease, box-shadow 0.4s ease, border-color 0.3s ease',
                willChange: 'transform',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 24px 60px rgba(47,93,80,0.1)';
                e.currentTarget.style.borderColor = '#2F5D50';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '0 20px 50px rgba(36,49,38,0.02)';
                e.currentTarget.style.borderColor = '#D7DDD4';
            }}
        >
            <Link to={`/product/${product.id}`} className="no-underline text-inherit block cursor-pointer">
                {/* Image frame */}
                <div className="relative w-full aspect-square rounded-[20px] bg-[#F8F7F3] flex items-center justify-center overflow-hidden mb-1.5 shadow-inner border border-[#D7DDD4]"
                    style={{ transition: 'transform 0.4s ease' }}
                >
                    <img
                        src={displayImage}
                        alt={displayTitle}
                        className="w-full h-full object-cover mix-blend-multiply"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/product-placeholder.png'; }}
                        loading="lazy"
                    />

                    {/* Product code badge */}
                    {displayCode && (
                        <span className="absolute top-2.5 left-2.5 bg-[#2F5D50] text-white font-black text-[10px] uppercase px-2.5 py-1 rounded-xl shadow-md border border-[#2F5D50] tracking-wider z-10">
                            {displayCode}
                        </span>
                    )}

                    {/* Wishlist button */}
                    <button
                        onClick={handleLikeClick}
                        className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 hover:bg-[#F2ECE4] flex items-center justify-center shadow-sm border border-[#D7DDD4] cursor-pointer z-20"
                        style={{ transition: 'transform 0.2s ease, background 0.2s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
                        aria-label="Add to list"
                    >
                        <Heart className={`w-4 h-4 ${isCurrentlyLiked ? 'fill-red-500 text-red-500' : 'text-[#5D6962]'}`} style={{ transition: 'color 0.2s ease, fill 0.2s ease' }} />
                    </button>
                </div>

                {/* Title & metadata */}
                <div className="text-left space-y-0.5 mb-1.5">
                    <h3 className="text-xs sm:text-sm font-black text-[#243126] leading-snug line-clamp-2 group-hover:text-[#2F5D50] m-0"
                        style={{ transition: 'color 0.3s ease' }}>
                        {displayTitle}
                    </h3>

                    <div className="flex items-center justify-between gap-1 pt-0.5">
                        <span className="text-[10px] font-black text-[#8BA888] uppercase tracking-wider truncate">
                            {displayBrand} | {displayType}
                        </span>
                    </div>

                    <div className="flex items-center justify-between pt-1 min-h-[20px]">

                        <span className="text-[11px] font-bold text-[#8B958E]">{displayPack}</span>
                        {product.stock !== undefined && product.stock < minimum_order && (
                            <span className="text-[9px] font-bold text-[#C46A42] uppercase bg-[rgba(196,106,66,0.08)] border border-[rgba(196,106,66,0.2)] px-2 py-0.5 rounded-md">
                                Out of Stock
                            </span>
                        )}
                    </div>
                </div>
            </Link>

            {/* Bottom action area */}
            <div className="pt-0 flex flex-col gap-1.5">
                {/* Row 1 */}
                <div className="flex flex-row items-center justify-between gap-1.5 sm:gap-2">
                    <div className="flex items-center justify-between bg-[#F8F7F3] border border-[#D7DDD4] rounded-xl px-1 sm:px-2 py-1 flex-1 max-w-[90px] sm:max-w-[104px] h-9 shrink-0">
                        <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDecrement(); }}
                            className="text-[#5D6962] hover:text-[#243126] font-black text-sm px-1.5 sm:px-2 leading-none bg-transparent border-none cursor-pointer h-full"
                        >-</button>
                        <input
                            type="number"
                            value={cartQuantity > 0
                                ? Math.max(cartQuantity, minimum_order)
                                : (quantity === '' ? '' : Math.max(Number(quantity) || 0, minimum_order))}
                            min={minimum_order}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onChange={(e) => {
                                e.preventDefault(); e.stopPropagation();
                                const val = e.target.value;
                                if (cartQuantity > 0) {
                                    if (val !== '') {
                                        const parsed = parseInt(val, 10);
                                        if (!isNaN(parsed) && parsed >= minimum_order) handleUpdateCartQty(product, parsed);
                                    }
                                } else {
                                    if (val === '') { setQuantity(''); }
                                    else {
                                        const parsed = parseInt(val, 10);
                                        if (!isNaN(parsed) && parsed >= minimum_order) setQuantity(parsed);
                                    }
                                }
                            }}
                            onBlur={(e) => {
                                e.preventDefault(); e.stopPropagation();
                                if (cartQuantity > 0) {
                                    const parsed = parseInt(e.target.value, 10);
                                    if (isNaN(parsed) || parsed < minimum_order) handleUpdateCartQty(product, minimum_order);
                                } else {
                                    if (quantity === '' || Number(quantity) < minimum_order) setQuantity(minimum_order);
                                }
                            }}
                            className="w-full min-w-0 text-center text-xs font-extrabold text-[#243126] bg-transparent border-none outline-none focus:ring-0 p-0"
                            placeholder={String(minimum_order)}
                        />
                        <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleIncrement(); }}
                            className="text-[#5D6962] hover:text-[#243126] font-black text-sm px-1.5 sm:px-2 leading-none bg-transparent border-none cursor-pointer h-full"
                        >+</button>
                    </div>

                    {/* Right side of Row 1: Price OR small Add button */}
                    {hasPricingForScheme ? (
                        <span className="font-black text-[17px] sm:text-[20px] text-black shrink min-w-0 text-right truncate leading-tight" title={`₹${formattedPrice}`}>₹{formattedPrice}</span>
                    ) : (
                        <button
                            onClick={(e) => {
                                e.preventDefault(); e.stopPropagation();
                                const qty = Number(quantity) || minimum_order;
                                if (onAddToCart) {
                                    onAddToCart(product, qty);
                                } else if (handleUpdateCartQty) {
                                    handleUpdateCartQty(product, (cartQuantity || 0) + qty);
                                }
                                if (cartQuantity === 0) setQuantity(minimum_order);
                            }}
                            className={`bg-[#2F5D50] hover:bg-[#244A40] text-white w-9 h-9 sm:w-auto sm:px-3 rounded-xl border-none cursor-pointer shrink-0 flex items-center justify-center gap-1.5 ${cartQuantity > 0 ? 'opacity-70' : ''}`}
                            style={{ transition: 'background 0.2s ease, opacity 0.2s ease' }}
                            title="Add to Cart"
                        >
                            <ShoppingCart className="w-4 h-4 shrink-0" />
                            <span className="hidden sm:block font-black text-xs">Add</span>
                        </button>
                    )}
                </div>

                {/* Row 2: Full width Add button OR Request Quote button */}
                {hasPricingForScheme ? (
                    <button
                        onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            const qty = Number(quantity) || minimum_order;
                            if (onAddToCart) {
                                onAddToCart(product, qty);
                            } else if (handleUpdateCartQty) {
                                handleUpdateCartQty(product, (cartQuantity || 0) + qty);
                            }
                            if (cartQuantity === 0) setQuantity(minimum_order);
                        }}
                        className={`w-full bg-[#2F5D50] hover:bg-[#244A40] text-white h-9 rounded-xl border-none cursor-pointer flex items-center justify-center gap-1.5 ${cartQuantity > 0 ? 'opacity-70' : ''}`}
                        style={{ transition: 'background 0.2s ease, opacity 0.2s ease' }}
                        title="Add to Cart"
                    >
                        <ShoppingCart className="w-4 h-4" />
                        <span className="font-black text-xs">Add</span>
                    </button>
                ) : (
                    <button
                        onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            openQuotePopup(product);
                        }}
                        className="w-full bg-[#C46A42] hover:bg-[#B75A37] text-white font-extrabold text-xs h-9 rounded-xl flex items-center justify-center gap-1.5 cursor-pointer shadow-xs border-none mt-0.5"
                        style={{ transition: 'background 0.2s ease' }}
                    >
                        <MessageCircle className="w-3.5 h-3.5 text-white" />
                        <span>Request Quote</span>
                    </button>
                )}
            </div>
        </div>
    );
});

export default ProductCard;
